'use client';

import { useState, useRef, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { CaretLeft, CaretRight, X } from '@phosphor-icons/react';
import { VisualizationWrapper } from './visualization-wrapper';
import { useVisualizationContext, VisualizationProvider } from './context';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { timelineColors, overlayColors } from './colors';

interface TimelineEvent {
  id: string;
  date: string;
  label: string;
  description?: string;
  importance: 'major' | 'minor' | 'milestone';
}

interface TimelineProps {
  element: {
    props: {
      title: string;
      events: Array<TimelineEvent>;
      startYear?: number;
      endYear?: number;
    };
    raw?: boolean;
    visualizationId?: string;
  };
}

// Fixed color styles for each importance level
const getImportanceStyles = (importance: 'major' | 'minor' | 'milestone') => {
  const colors = timelineColors.importance[importance];
  return {
    card: {
      background: colors.bg,
      borderColor: colors.border,
    },
    dot: {
      background: colors.dot,
      borderColor: colors.dot,
    },
    text: colors.text,
    connector: colors.dot,
  };
};

function TimelineEventCard({ 
  event, 
  position, 
  onClick,
}: { 
  event: TimelineEvent; 
  position: 'top' | 'bottom';
  onClick: () => void;
}) {
  const styles = getImportanceStyles(event.importance);
  const isTop = position === 'top';

  return (
    <div 
      className={cn(
        "flex flex-col items-center shrink-0 cursor-pointer group w-40 sm:w-48 md:w-56",
        isTop ? "flex-col" : "flex-col-reverse"
      )}
    >
      {/* Card */}
      <motion.div
        whileHover={{ scale: 1.02, y: isTop ? -4 : 4 }}
        whileTap={{ scale: 0.98 }}
        onClick={onClick}
        className="w-full rounded-xl border-2 p-3 sm:p-4 transition-shadow hover:shadow-lg"
        style={{
          backgroundColor: styles.card.background,
          borderColor: styles.card.borderColor,
        }}
      >
        <div 
          className="text-xs font-mono px-2 py-0.5 rounded-full w-fit mb-2 border"
          style={{ 
            backgroundColor: timelineColors.background,
            color: styles.text,
            borderColor: styles.card.borderColor,
          }}
        >
          {event.date}
        </div>
        <h4
          className="font-semibold text-sm sm:text-base"
          style={{ color: styles.text }}
        >
          {event.label}
        </h4>
        {event.description && (
          <p
            className="text-xs mt-1"
            style={{ color: timelineColors.text.secondary }}
          >
            {event.description}
          </p>
        )}
      </motion.div>

      {/* Connector line */}
      <div 
        className="w-0.5 h-6 sm:h-8"
        style={{ backgroundColor: styles.connector }}
      />

      {/* Dot on timeline */}
      <div 
        className="w-4 h-4 sm:w-5 sm:h-5 rounded-full border-2 flex items-center justify-center transition-transform group-hover:scale-125"
        style={{
          backgroundColor: styles.dot.background,
          borderColor: styles.dot.borderColor,
        }}
      >
        {event.importance === 'milestone' && (
          <span className="text-[10px] text-white">★</span>
        )}
      </div>
    </div>
  );
}

export function Timeline({ element }: TimelineProps) {
  if (!element?.props?.events?.length) {
    return <div className="p-4 border rounded-lg bg-muted/30 text-muted-foreground">Loading timeline...</div>;
  }
  
  const { title, events } = element.props;

  // Raw mode: render without wrapper for canvas - use fit mode
  if (element.raw) {
    return (
      <VisualizationProvider value={{ viewMode: 'fit', isFullscreen: false, scale: 1, containerWidth: 1920 }}>
        <div className="w-full h-full">
          <TimelineContent events={events} />
        </div>
      </VisualizationProvider>
    );
  }

  return (
    <VisualizationWrapper
      title={title}
      minHeight={520}
      allowFitToggle={true}
      exportData={{
        type: 'timeline',
        content: element.props
      }}
      visualizationId={element.visualizationId}
    >
      <TimelineContent events={events} />
    </VisualizationWrapper>
  );
}

function TimelineContent({ events }: { events: TimelineEvent[] }) {
  const scrollRef = useRef<HTMLDivElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);
  const { viewMode, containerWidth } = useVisualizationContext();
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(true);
  const [selectedEvent, setSelectedEvent] = useState<TimelineEvent | null>(null);
  const [scale, setScale] = useState(1);
  const [contentWidth, setContentWidth] = useState(0);

  // Handle escape key to close detail modal
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && selectedEvent) {
        e.stopPropagation();
        e.preventDefault();
        setSelectedEvent(null);
      }
    };

    if (selectedEvent) {
      document.addEventListener('keydown', handleKeyDown, true);
    }

    return () => {
      document.removeEventListener('keydown', handleKeyDown, true);
    };
  }, [selectedEvent]);

  // Calculate scale for fit mode
  useEffect(() => {
    if (contentRef.current && containerRef.current) {
      const fullWidth = contentRef.current.scrollWidth;
      const availableWidth = containerRef.current.clientWidth;
      setContentWidth(fullWidth);
      
      if (viewMode === 'fit' && fullWidth > availableWidth) {
        const newScale = Math.max(0.3, availableWidth / fullWidth);
        setScale(newScale);
      } else {
        setScale(1);
      }
    }
  }, [viewMode, events.length, containerWidth]);

  const checkScroll = () => {
    if (scrollRef.current && viewMode === 'scroll') {
      const { scrollLeft, scrollWidth, clientWidth } = scrollRef.current;
      setCanScrollLeft(scrollLeft > 10);
      setCanScrollRight(scrollLeft < scrollWidth - clientWidth - 10);
    }
  };

  useEffect(() => {
    checkScroll();
    const ref = scrollRef.current;
    if (ref) {
      ref.addEventListener('scroll', checkScroll);
      window.addEventListener('resize', checkScroll);
    }
    return () => {
      if (ref) ref.removeEventListener('scroll', checkScroll);
      window.removeEventListener('resize', checkScroll);
    };
  }, [viewMode]);

  const scroll = (direction: 'left' | 'right') => {
    if (scrollRef.current) {
      const scrollAmount = scrollRef.current.clientWidth * 0.6;
      scrollRef.current.scrollBy({
        left: direction === 'left' ? -scrollAmount : scrollAmount,
        behavior: 'smooth',
      });
    }
  };

  // Calculate scaled height for fit mode container
  const scaledHeight = viewMode === 'fit' && contentRef.current 
    ? contentRef.current.offsetHeight * scale 
    : undefined;

  return (
    <div 
      ref={containerRef}
      className="relative w-full h-full"
      style={{ backgroundColor: timelineColors.background }}
    >
      {/* Scroll buttons - only show in scroll mode */}
      {viewMode === 'scroll' && (
        <AnimatePresence>
          {canScrollLeft && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="absolute left-2 top-1/2 -translate-y-1/2 z-20"
            >
              <button
                className="rounded-full shadow-lg h-10 w-10 flex items-center justify-center transition-colors"
                style={{ 
                  backgroundColor: timelineColors.cardBackground,
                  border: `1px solid ${timelineColors.border}`,
                  color: timelineColors.text.primary,
                }}
                onClick={() => scroll('left')}
              >
                <CaretLeft weight="bold" className="size-5" />
              </button>
            </motion.div>
          )}
          {canScrollRight && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="absolute right-2 top-1/2 -translate-y-1/2 z-20"
            >
              <button
                className="rounded-full shadow-lg h-10 w-10 flex items-center justify-center transition-colors"
                style={{ 
                  backgroundColor: timelineColors.cardBackground,
                  border: `1px solid ${timelineColors.border}`,
                  color: timelineColors.text.primary,
                }}
                onClick={() => scroll('right')}
              >
                <CaretRight weight="bold" className="size-5" />
              </button>
            </motion.div>
          )}
        </AnimatePresence>
      )}

      {/* Gradient fade edges - only in scroll mode */}
      {viewMode === 'scroll' && (
        <>
          <div 
            className="absolute left-0 top-0 bottom-0 w-12 z-10 pointer-events-none" 
            style={{ background: `linear-gradient(to right, ${timelineColors.background}, transparent)` }}
          />
          <div 
            className="absolute right-0 top-0 bottom-0 w-12 z-10 pointer-events-none" 
            style={{ background: `linear-gradient(to left, ${timelineColors.background}, transparent)` }}
          />
        </>
      )}

      {/* Timeline content */}
      <div
        ref={scrollRef}
        className={cn(
          "w-full h-full scrollbar-thin scrollbar-thumb-muted-foreground/20 scrollbar-track-transparent",
          viewMode === 'scroll' ? "overflow-x-auto overflow-y-auto" : "overflow-hidden flex items-center justify-center"
        )}
        style={{ 
          scrollbarWidth: 'thin',
          ...(viewMode === 'fit' && scaledHeight ? { minHeight: scaledHeight + 40 } : {})
        }}
      >
        <div 
          ref={contentRef}
          className="min-w-max h-full flex flex-col justify-center py-6 px-16"
          style={viewMode === 'fit' ? {
            transform: `scale(${scale})`,
            transformOrigin: 'center center',
          } : undefined}
        >
          {/* Top row of events (even indices) */}
          <div className="flex items-end gap-4 sm:gap-6 md:gap-8 mb-0">
            {events.filter((_, i) => i % 2 === 0).map((event) => (
              <TimelineEventCard
                key={event.id}
                event={event}
                position="top"
                onClick={() => setSelectedEvent(event)}
              />
            ))}
          </div>

          {/* Timeline line */}
          <div className="relative h-1 my-2">
            <div 
              className="absolute inset-0 rounded-full" 
              style={{ 
                background: `linear-gradient(to right, ${timelineColors.line.gradient.join(', ')})` 
              }}
            />
          </div>

          {/* Bottom row of events (odd indices) */}
          <div className="flex items-start gap-4 sm:gap-6 md:gap-8 mt-0 pl-24 sm:pl-28 md:pl-32">
            {events.filter((_, i) => i % 2 === 1).map((event) => (
              <TimelineEventCard
                key={event.id}
                event={event}
                position="bottom"
                onClick={() => setSelectedEvent(event)}
              />
            ))}
          </div>
        </div>
      </div>

      {/* Detail Modal */}
      <AnimatePresence>
        {selectedEvent && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="absolute inset-0 z-30 flex items-center justify-center p-4"
            style={{ backgroundColor: overlayColors.backdrop }}
            onClick={() => setSelectedEvent(null)}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              className="rounded-xl border-2 p-6 max-w-md w-full"
              style={{
                backgroundColor: overlayColors.card.background,
                borderColor: overlayColors.card.border,
                boxShadow: overlayColors.card.shadow,
              }}
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex justify-between items-start mb-4">
                <div 
                  className="text-sm font-mono px-3 py-1 rounded-full border"
                  style={{ 
                    color: getImportanceStyles(selectedEvent.importance).text,
                    borderColor: timelineColors.importance[selectedEvent.importance].border,
                  }}
                >
                  {selectedEvent.date}
                </div>
                <button
                  className="h-8 w-8 -mt-2 -mr-2 rounded-md flex items-center justify-center hover:bg-gray-100 transition-colors"
                  style={{ color: overlayColors.text.secondary }}
                  onClick={() => setSelectedEvent(null)}
                >
                  <X weight="bold" className="size-4" />
                </button>
              </div>
              <h3 
                className="text-xl font-bold mb-3"
                style={{ color: getImportanceStyles(selectedEvent.importance).text }}
              >
                {selectedEvent.label}
              </h3>
              {selectedEvent.description && (
                <p 
                  className="leading-relaxed"
                  style={{ color: overlayColors.text.secondary }}
                >
                  {selectedEvent.description}
                </p>
              )}
              <div 
                className="mt-4 pt-4 border-t flex items-center gap-2 text-xs"
                style={{ 
                  borderColor: timelineColors.border,
                  color: overlayColors.text.secondary,
                }}
              >
                <span 
                  className="px-2 py-0.5 rounded-full capitalize"
                  style={{
                    backgroundColor: timelineColors.importance[selectedEvent.importance].bg,
                    color: timelineColors.importance[selectedEvent.importance].text,
                  }}
                >
                  {selectedEvent.importance}
                </span>
                event
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
